/**
 * SatelliteBomber - Horizontal-moving enemy that drops missiles
 * Crosses the screen horizontally at high altitude, dropping missiles on cities below.
 * Introduced at wave 9+.
 */

export class SatelliteBomber {
  constructor({ startX, y, speed, direction, gameWidth, gameHeight, dropInterval }) {
    this.x = startX;
    this.y = y;
    this.speed = speed;
    this.direction = direction; // 1 = right, -1 = left
    this.vx = speed * direction;
    this.vy = 0;
    this.gameWidth = gameWidth;
    this.gameHeight = gameHeight;
    this.alive = true;
    this.type = 'satellite';
    this.color = '#44ff88';
    this.trailColor = '#22aa55';

    // Drop management
    this.dropInterval = dropInterval || 1.5;
    this.dropTimer = 0;
    this.maxDrops = 3;
    this.dropsRemaining = this.maxDrops;
    this.droppedMissiles = []; // Populated each update

    // Visual
    this.trail = [{ x: this.x, y: this.y }];
    this.bodyWidth = 12;
    this.bodyHeight = 5;
    this.pulsePhase = Math.random() * Math.PI * 2;

    // For collision compatibility
    this.targetX = direction > 0 ? gameWidth + 50 : -50;
    this.targetY = y;
  }

  update(dt) {
    if (!this.alive) return;

    this.droppedMissiles = []; // Reset each frame
    this.pulsePhase += dt * 4;

    // Move horizontally
    this.x += this.vx * dt;

    // Trail
    this.trail.push({ x: this.x, y: this.y });
    if (this.trail.length > 40) {
      this.trail.shift();
    }

    // Drop missiles at interval
    this.dropTimer += dt;
    if (this.dropTimer >= this.dropInterval && this.dropsRemaining > 0) {
      this.dropTimer = 0;
      this.dropsRemaining--;
      this._dropMissile();
    }

    // Check if off screen
    if ((this.direction > 0 && this.x > this.gameWidth + 50) ||
        (this.direction < 0 && this.x < -50)) {
      this.alive = false;
    }
  }

  _dropMissile() {
    // Drop a missile from current position aimed at a random target below
    const targetX = this.gameWidth * (0.1 + Math.random() * 0.8);
    const targetY = this.gameHeight - 30; // Ground level

    this.droppedMissiles.push({
      x: this.x,
      y: this.y,
      targetX,
      targetY,
      speed: 60 + Math.random() * 40,
      color: '#44ff88',
      trailColor: '#22aa55',
    });
  }

  render(ctx) {
    if (!this.alive && this.trail.length === 0) return;

    // Trail - dashed/dotted effect
    if (this.trail.length > 1) {
      for (let i = 1; i < this.trail.length; i++) {
        const alpha = (i / this.trail.length) * 0.4;
        ctx.strokeStyle = this.trailColor;
        ctx.globalAlpha = alpha;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(this.trail[i - 1].x, this.trail[i - 1].y);
        ctx.lineTo(this.trail[i].x, this.trail[i].y);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    if (!this.alive) return;

    ctx.save();

    // Pulsing glow
    const pulse = 0.7 + 0.3 * Math.sin(this.pulsePhase);

    // Body - elongated shape
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 8 * pulse;
    ctx.fillStyle = this.color;

    // Main body (elongated hexagon)
    ctx.beginPath();
    const bw = this.bodyWidth;
    const bh = this.bodyHeight;
    ctx.moveTo(this.x - bw, this.y);
    ctx.lineTo(this.x - bw * 0.6, this.y - bh);
    ctx.lineTo(this.x + bw * 0.6, this.y - bh);
    ctx.lineTo(this.x + bw, this.y);
    ctx.lineTo(this.x + bw * 0.6, this.y + bh);
    ctx.lineTo(this.x - bw * 0.6, this.y + bh);
    ctx.closePath();
    ctx.fill();

    // Wings
    ctx.fillStyle = '#33cc66';
    ctx.beginPath();
    ctx.moveTo(this.x - bw * 0.4, this.y - bh);
    ctx.lineTo(this.x - bw * 0.2, this.y - bh * 2.5);
    ctx.lineTo(this.x + bw * 0.2, this.y - bh * 2.5);
    ctx.lineTo(this.x + bw * 0.4, this.y - bh);
    ctx.closePath();
    ctx.fill();

    // Center light
    ctx.fillStyle = '#ffffff';
    ctx.globalAlpha = pulse;
    ctx.beginPath();
    ctx.arc(this.x, this.y, 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }
}
